import { Helmet } from "react-helmet-async";
import { ArrowRight, LogIn, Shield, Crown, Eye, EyeOff, Globe } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { useSubscription } from "@/hooks/useSubscription";
import { useResearchAccessMode } from "@/hooks/useResearchAccessMode";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ResearchContentItem } from "@/components/research/WorkflowCard";
import { ClubSubscribeModal as SubscribeModal, ClubClosedNotice, useClubJoinOpen } from "@/components/club/ClubCheckout";
import { useGeoCurrency } from "@/hooks/useGeoCurrency";
import { pillars } from "@/components/club/ClubPillarsGrid";
import { RecentDrops } from "@/components/club/RecentDrops";
import { FounderNote } from "@/components/club/FounderNote";
import { ReferralBanner } from "@/components/club/ReferralBanner";
import { SN_MembershipCTA } from "@/components/SN_MembershipCTA";
import { premiumIncludes, foundingIncludes } from "@/components/SN_MembershipPricingCompare";
import { faqs } from "@/components/SN_MembershipFAQ";
import { ClubFoundingModal as FoundingMemberModal } from "@/components/club/ClubCheckout";
import { PaymentProcessorsBadge } from "@/components/icons/PaymentLogos";
import { PortfolioChart } from "@/components/club/PortfolioChart";
import { Panel, W } from "@/platform/ui";
import { Faq, IncludedTable, OfferFacts, OfferHeader, OfferSec, OfferShell, PricePanel, QuotePanel, TermsLine, offerBtn, useFeaturedQuotes } from "@/platform/offer";

// The Club page in the offer kit (26 Oct 2026, phase 0 of the old-pages plan): the plans side by side,
// what members get as a table, the sample workspace view and the sign-in box. Every buy and sign-in
// control is the same component with the same handler as before; only its frame changed.

const ctaLinks = [
  { href: "https://greeksoup.ai", label: "Explore greeksoup.ai", external: true },
  { href: "/ai-financial-research", label: "Book a Consultation" },
];

// Only fetch safe metadata columns. file_url, external_url, and body_html were
// revoked from anon/authenticated roles by the research_content column-grant
// hardening migration, so requesting them here returns a permission error and
// blanks the entire card list. Content-type badges still work via
// content_type + external_url_type alone.
const CARD_COLUMNS = "id, title, subtitle, category, content_type, tools, read_time, external_url_type, thumbnail_url, is_premium, display_order, slug, published_date, product_id, is_pinned";

const ResearchInAction = () => {
  const { user, isAdmin } = useAuth();
  // the Club takes no new members (his follow-up, 30 Oct 2026): only signed-in members see join buttons
  const { open: joinOpen, waiting: joinWaiting } = useClubJoinOpen();
  const { isSubscribed, subscription } = useSubscription(user);
  const { mode, toggle, effectiveIsAdmin } = useResearchAccessMode(isAdmin);
  const [content, setContent] = useState<ResearchContentItem[]>([]);
  const [clickCounts, setClickCounts] = useState<Record<string, number>>({});
  const [youtubeByContent, setYoutubeByContent] = useState<Record<string, string>>({});
  
  const [isLoadingContent, setIsLoadingContent] = useState(true);
  const [sortBy] = useState<"popular" | "recent">("popular");
  const [showSubscribe, setShowSubscribe] = useState(false);
  const { isIndian, subscriptionPrices } = useGeoCurrency();
  // the annual plan against twelve monthly payments, from the prices shown (₹ 17%, $ 15%, as the payment window says)
  const num = (p: string) => Number(p.replace(/[^0-9.]/g, ""));
  const annualSaving = Math.round((1 - num(subscriptionPrices.base.annual) / (12 * num(subscriptionPrices.base.monthly))) * 100);
  // The Club's live counts (as the old trust bar read them), the founding window and the quotes.
  const [members, setMembers] = useState<number | null>(null);
  const [playbooks, setPlaybooks] = useState<number | null>(null);
  useEffect(() => {
    let active = true;
    (async () => {
      const [mRes, pRes] = await Promise.all([
        supabase.rpc("get_club_member_count"),
        supabase.from("research_content").select("id", { count: "exact", head: true })
          .eq("is_published", true).is("deleted_at", null),
      ]);
      if (!active) return;
      setMembers(typeof mRes.data === "number" ? mRes.data : null);
      setPlaybooks(pRes.count ?? null);
    })();
    return () => { active = false; };
  }, []);
  const quotes = useFeaturedQuotes(3);

  // Founding Member: the same handler as the old pricing block, unchanged.
  const [foundingOpen, setFoundingOpen] = useState(false);
  const navigate = useNavigate();
  const handleFoundingClick = () => {
    if (!user) {
      const redirect = encodeURIComponent("/?founding=1");
      navigate(`/login?redirect=${redirect}`);
      return;
    }
    setFoundingOpen(true);
  };
  const perMonthAnnualised = isIndian ? "₹833" : "$33";
  const foundingPrice = isIndian ? "₹39,999" : "$999";
  const gst = isIndian ? " Exclusive of GST." : "";

  const sortedContent = useMemo(() => {
    return [...content].sort((a, b) => {
      if (sortBy === "recent") {
        const dateA = new Date(a.published_date || "1970-01-01").getTime();
        const dateB = new Date(b.published_date || "1970-01-01").getTime();
        return dateB - dateA;
      }
      const pinnedA = (a as any).is_pinned ? 1 : 0;
      const pinnedB = (b as any).is_pinned ? 1 : 0;
      if (pinnedA !== pinnedB) return pinnedB - pinnedA;
      const clicksA = clickCounts[a.id] || 0;
      const clicksB = clickCounts[b.id] || 0;
      if (clicksA !== clicksB) return clicksB - clicksA;
      return (a.display_order || 0) - (b.display_order || 0);
    });
  }, [content, sortBy, clickCounts]);

  useEffect(() => {
    const fetchContent = async () => {
      const [contentRes, clicksRes, ytRes] = await Promise.all([
        supabase
          .from("research_content")
          .select(CARD_COLUMNS)
          .eq("is_published", true)
          .is("deleted_at", null)
          .order("display_order", { ascending: true }) as any,
        supabase
          .from("research_content_clicks")
          .select("content_id"),
        supabase
          .from("research_content_files")
          .select("content_id, url")
          .eq("kind", "link")
          .eq("link_type", "youtube") as any,
      ]);

      if (!contentRes.error && contentRes.data) {
        setContent(contentRes.data as ResearchContentItem[]);
      }

      if (!clicksRes.error && clicksRes.data) {
        const counts: Record<string, number> = {};
        clicksRes.data.forEach((row: any) => {
          counts[row.content_id] = (counts[row.content_id] || 0) + 1;
        });
        setClickCounts(counts);
      }

      if (!ytRes.error && ytRes.data) {
        const map: Record<string, string> = {};
        (ytRes.data as Array<{ content_id: string; url: string }>).forEach((row) => {
          if (row.content_id && row.url && !map[row.content_id]) {
            map[row.content_id] = row.url;
          }
        });
        setYoutubeByContent(map);
      }

      setIsLoadingContent(false);
    };
    fetchContent();
  }, []);

  // Smoothly scroll to a hash target (e.g. #pricing from email CTAs) once the
  // page has rendered. The default browser jump can fire before sections like
  // the pricing compare card are in the DOM, so we retry briefly until found.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash?.replace("#", "");
    if (!hash) return;
    let tries = 0;
    const tick = () => {
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      if (tries++ < 30) setTimeout(tick, 100);
    };
    tick();
  }, []);

  // Stamp first Alpha Club visit on referrals row (idempotent, fire & forget).
  useEffect(() => {
    if (!user) return;
    const KEY = "ref_club_visit_v1";
    try {
      if (sessionStorage.getItem(KEY) === user.id) return;
      sessionStorage.setItem(KEY, user.id);
    } catch {}
    supabase.functions.invoke("referral-stamp-event", { body: { event: "club_visit" } }).catch(() => {});
  }, [user]);

  return (
    <>
      <Helmet>
        <title>Alpha with AI Club | Shikshan Nivesh</title>
        <meta name="description" content="Real problems. AI solutions. From our client work. Explore AI workflows for equity research, market analysis, and investment due diligence." />
        <link rel="canonical" href="https://shikshannivesh.com/ai-investing-club" />
        <meta property="og:title" content="Alpha with AI Club - Shikshan Nivesh" />
        <meta property="og:description" content="Real problems. AI solutions. From our client work. Explore AI workflows for equity research, market analysis, and investment due diligence." />
        <meta property="og:url" content="https://shikshannivesh.com/ai-investing-club" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Alpha with AI Club",
            url: "https://shikshannivesh.com/ai-investing-club",
            description:
              "AI-powered research playbooks and workflows for equity research, market analysis, and investment due diligence.",
            hasPart: {
              "@type": "ItemList",
              itemListElement: sortedContent.slice(0, 30).map((item, i) => ({
                "@type": "ListItem",
                position: i + 1,
                url: `https://shikshannivesh.com/ai-investing-club/${item.slug}`,
                name: item.title,
              })),
            },
          })}
        </script>
      </Helmet>

      <OfferShell>
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
            })}
          </script>
        </Helmet>

        <OfferHeader
          crumbs={[["/", "Home"], [null, "Alpha with AI Club"]]}
          label="Club · Membership"
          title="Alpha with AI Club"
          lead={
            <>
              <p className="font-medium text-foreground">Our AI research playbooks, the model book and the paid newsletter, in one membership.</p>
            </>
          }
          aside={
            /* The access state: sign in, subscribe, or enter. Logic untouched for members; everyone else
               (signed out, or an account made after the Club closed) gets the closed notice. */
            <Panel title={user ? "Your access" : "Access"}>
              {joinWaiting ? <div className="px-4 py-4" /> : !joinOpen ? <div className="px-4 py-4"><ClubClosedNotice /></div> : (
              <div className="px-4 py-4">
                <AccessStateBanner
                  user={user}
                  isAdmin={isAdmin}
                  isSubscribed={isSubscribed}
                  subscription={subscription}
                  mode={mode}
                  onToggleMode={toggle}
                  onSubscribe={() => setShowSubscribe(true)}
                  isIndian={isIndian}
                  monthlyPrice={subscriptionPrices.base.monthly}
                />
                {user && (
                  <Link
                    to="/ai-investing-club/community"
                    className="group mt-4 inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-primary px-[18px] text-[14px] font-semibold text-primary-foreground transition hover:brightness-110"
                  >
                    Enter the Club
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-[3px]" />
                  </Link>
                )}
                {user && (
                  <div className="mt-4 border-t border-[var(--sn-border)] pt-4">
                    <ReferralBanner />
                  </div>
                )}
              </div>
              )}
            </Panel>
          }
        >
          <div className="mt-4 flex flex-wrap items-center gap-5 text-[13.5px]">
            <a href="#inside" className="inline-flex items-center gap-1.5 font-medium text-foreground hover:underline">What is inside <ArrowRight className="h-3.5 w-3.5" /></a>
            <a href="#playbooks" className="inline-flex items-center gap-1.5 font-medium text-foreground hover:underline">Recent drops <ArrowRight className="h-3.5 w-3.5" /></a>
          </div>
        </OfferHeader>

        <W>
          <OfferFacts
            className="mt-6"
            items={[
              { label: "Members", value: members != null ? members.toLocaleString("en-US") : "–" },
              { label: "Playbooks", value: playbooks != null ? playbooks.toLocaleString("en-US") : "–" },
              { label: "Countries", value: "25" },
              { label: "Founded", value: "2026" },
              { label: "Founder", value: <span className="font-sans">Shubham Borkar</span> },
              { label: "Annual, per month", value: perMonthAnnualised, foot: "Billed annually" },
            ]}
          />

          {/* Plans: the old monthly and annual toggle shows both prices at once now; one Start membership, as before. */}
          <OfferSec id="pricing" title="Membership pricing" lead="Auto-renews. Cancel anytime from your dashboard.">
            {!joinWaiting && !joinOpen && <ClubClosedNotice compact className="mb-4" />}
            <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
              <PricePanel
                lead
                title="Alpha with AI Club · Premium"
                cells={[
                  { label: "Monthly", price: subscriptionPrices.base.monthly, unit: "month", note: `Billed monthly. Cancel anytime.${gst}` },
                  { label: <>Annual <span className="text-foreground">· saves about {annualSaving}%</span></>, price: subscriptionPrices.base.annual, unit: "year", note: `${perMonthAnnualised}/month equivalent. Billed annually.${gst}` },
                ]}
                includes={premiumIncludes}
                action={joinOpen ? <SN_MembershipCTA showIcon={false} startLabel="Start membership" className={offerBtn.solid} /> : null}
              />
              <PricePanel
                title="Founding Member · Price locked for life"
                cells={[{ label: "Annual", price: foundingPrice, unit: "year", note: `Auto-renews annually. Your founding rate is locked for life.${gst}` }]}
                includes={foundingIncludes}
                action={joinOpen ? (
                  <button type="button" onClick={handleFoundingClick} className={offerBtn.plain}>
                    Become a Founding Member
                  </button>
                ) : null}
              />
            </div>
            <TermsLine
              note="The model portfolio is a model book. The positions in it are hypothetical and no real capital sits behind them. Nothing on this page is investment advice."
              links={[["/legal?section=refund", "Refunds"], ["/legal?section=terms", "Terms"], ["/legal?section=disclaimer", "Disclaimer"]]}
              right={<PaymentProcessorsBadge />}
            />
          </OfferSec>

          <OfferSec
            id="inside"
            title="What members get"
            lead={<>Most &ldquo;AI for investing&rdquo; content stops at theory. This club doesn&apos;t. Every playbook comes from real client work, distilled into something you can run the same afternoon.</>}
          >
            <IncludedTable head={["Item", "What you get", "Plan"]} rows={pillars.map((p) => [p.title.replace(/\.$/, ""), p.body, "Premium"])} />
          </OfferSec>

          <div className="grid gap-x-6 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">
            <OfferSec title="The model book">
              {/* One slot: the model book's live chart (Datawrapper gaSYC, fed after each US close); greeksoup.ai's portfolio
                  performance embed takes its place when that ships (his note, 26 Oct 2026) */}
              <PortfolioChart />
            </OfferSec>
            <OfferSec title="From the founder">
              <Panel className="px-4 py-4 [&_.mt-10]:mt-0 [&_p]:text-[14.5px] [&_.space-y-5]:space-y-3">
                <FounderNote />
              </Panel>
            </OfferSec>
          </div>

          <OfferSec id="playbooks" title="Recent drops">
            <Panel className="[&>div]:mt-0 [&_a]:px-4 [&>div>a:first-child]:border-t-0 [&>div>p]:mt-0 [&>div>p]:px-4 [&>div>p]:py-3">
              <RecentDrops />
            </Panel>
          </OfferSec>

          <div className="grid gap-x-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <OfferSec title="Questions">
              <Faq
                items={faqs.map((f) => ({
                  q: f.q,
                  a: (
                    <>
                      <p>{f.a}</p>
                      {f.href &&
                        (f.href.startsWith("http") ? (
                          <a href={f.href} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-[13.5px] font-medium text-foreground hover:underline">{f.proof} &rarr;</a>
                        ) : (
                          <Link to={f.href} className="mt-2 inline-block text-[13.5px] font-medium text-foreground hover:underline">{f.proof} &rarr;</Link>
                        ))}
                    </>
                  ),
                }))}
              />
            </OfferSec>
            <div>
            {quotes && quotes.length > 0 && (
              <OfferSec title="Why members stay">
                <QuotePanel title="Members" rows={quotes} />
              </OfferSec>
            )}
            <OfferSec title="Other paths">
              <Panel>
                <ul className="divide-y divide-[var(--sn-border)]">
                  {ctaLinks.map((link) => {
                    const cls = "group flex items-center justify-between gap-4 px-4 py-3 text-[14px] font-medium text-foreground hover:bg-[var(--snd-wash)]";
                    const body = <>{link.label}<ArrowRight className="h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform group-hover:translate-x-[3px]" /></>;
                    return (
                      <li key={link.label}>
                        {link.external ? (
                          <a href={link.href} target="_blank" rel="noopener noreferrer" className={cls}>{body}</a>
                        ) : (
                          <Link to={link.href} className={cls}>{body}</Link>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </Panel>
            </OfferSec>
            </div>
          </div>
        </W>
      </OfferShell>

      <SubscribeModal isOpen={showSubscribe} onClose={() => setShowSubscribe(false)} />
      <FoundingMemberModal isOpen={foundingOpen} onClose={() => setFoundingOpen(false)} />
    </>
  );
};

/* ── Access State Banner ── */

function AccessStateBanner({
  user,
  isAdmin,
  isSubscribed,
  subscription,
  mode,
  onToggleMode,
  onSubscribe,
  isIndian,
  monthlyPrice,
}: {
  user: any;
  isAdmin: boolean;
  isSubscribed: boolean;
  subscription: any;
  mode: "admin" | "member";
  onToggleMode: () => void;
  onSubscribe: () => void;
  isIndian: boolean;
  monthlyPrice: string;
}) {
  if (!user) {
    return (
      <div>
        <p className="text-[15px] leading-[1.55] text-muted-foreground">Free previews are open. Sign in to start exploring; members unlock the full library.</p>
        <Link to="/login?redirect=/ai-investing-club" className="mt-5 inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-primary px-[18px] text-[14px] font-semibold text-primary-foreground transition hover:brightness-110">
          <LogIn className="w-4 h-4" />
          Sign in to start exploring
        </Link>
      </div>
    );
  }

  if (isAdmin) {
    const inAdminMode = mode === "admin";
    return (
      <div className="flex flex-col gap-3">
        <div className={`inline-flex items-center gap-2 text-[14px] ${
          inAdminMode ? "" : ""
        }`}>
          {inAdminMode ? (
            <>
              <Shield className="w-4 h-4 text-primary" />
              <span className="text-primary font-medium">Admin view active</span>
              {isSubscribed && (
                <span className="text-muted-foreground ml-1">
                  (also subscribed: {subscription?.plan_name || subscription?.plan_type || "Premium"})
                </span>
              )}
            </>
          ) : (
            <>
              <Eye className="w-4 h-4 text-muted-foreground" />
              <span className="text-foreground font-medium">Viewing as regular member</span>
              {isSubscribed ? (
                <span className="text-foreground ml-1">
                  • Premium active - {subscription?.plan_name || subscription?.plan_type || "Premium"}
                </span>
              ) : (
                <span className="text-muted-foreground ml-1">• No premium membership</span>
              )}
            </>
          )}
        </div>
        <button
          onClick={onToggleMode}
          className="inline-flex items-center gap-1.5 self-start rounded-lg border border-[var(--sn-border-strong)] px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          {inAdminMode ? (
            <>
              <EyeOff className="w-3.5 h-3.5" />
              Switch to member view
            </>
          ) : (
            <>
              <Shield className="w-3.5 h-3.5" />
              Switch to admin view
            </>
          )}
        </button>
      </div>
    );
  }

  if (isSubscribed) {
    return (
      <div className="inline-flex items-center gap-2 text-[15px]">
        <Crown className="w-4 h-4 text-primary" />
        <span className="text-foreground font-medium">
          Premium active
          {subscription?.plan_name ? ` - ${subscription.plan_name}` : subscription?.plan_type ? ` - ${subscription.plan_type}` : ""}
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="text-[15px] leading-[1.55] text-muted-foreground">
        <span>
          Unlock all premium playbooks from{" "}
           <span className="text-primary font-semibold">
            {monthlyPrice}/month
          </span>
          {isIndian && (
            <span className="text-muted-foreground">, exclusive of GST</span>
          )}
        </span>
      </div>
      <button
        onClick={onSubscribe}
        className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-primary px-[18px] text-[14px] font-semibold text-primary-foreground transition hover:brightness-110"
      >
        <Crown className="w-3.5 h-3.5" />
        Subscribe to Premium
      </button>
      <p className="inline-flex items-center gap-1.5 font-mono text-[12px] text-muted-foreground">
        <Globe className="w-3.5 h-3.5 text-primary" />
        Now accepting international cards.
      </p>
    </div>
  );
}

export default ResearchInAction;
